import { Dialog } from 'radix-ui'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { overlays } from './store'

/** What a confirmation says and does. Everything is read once, at open (#209). */
export interface ConfirmOptions {
  title: ReactNode
  /** The sentence under the title: what happens, and whether it can be undone. */
  body?: ReactNode
  /** The action's verb, the primary button's label: 'Delete', 'Revoke', 'Publish'. */
  action: string
  /** Paints the action red: something is lost for good. */
  danger?: boolean
  onConfirm: () => void
  /** The secondary button's label, 'Cancel' unless it does something else. */
  cancel?: string
  /** Runs on a press of the secondary button only — not on Escape. */
  onCancel?: () => void
}

/**
 * Ask before doing something that cannot be taken back.
 *
 * `confirm({ title, body, action: 'Delete', danger, onConfirm })` — the options
 * are snapshot by the store at this call, so the title the reader saw is the
 * one they watch fade out, even after the page has dropped whatever it named.
 */
export function confirm(options: ConfirmOptions): void {
  overlays.open('confirm', options)
}

/**
 * The one confirmation panel: title, body, Cancel and the action. Pressing
 * either closes it; the action runs its `onConfirm` first. It is an
 * `alertdialog` (the host's `role`), so a click outside does not dismiss it.
 *
 * `children` sits between the text and the buttons, for a confirmation that
 * has to show more before it may be pressed (deleting a source counts what
 * goes first); `disabled` holds the action until then.
 */
export function ConfirmDialog({
  title,
  body,
  action,
  danger = false,
  onConfirm,
  cancel = 'Cancel',
  onCancel,
  close,
  children,
  disabled = false,
}: ConfirmOptions & { close: () => void; children?: ReactNode; disabled?: boolean }) {
  return (
    <>
      <div className="flex flex-col gap-2 text-center sm:text-left">
        <Dialog.Title className="font-semibold text-lg">{title}</Dialog.Title>
        {body ? (
          <Dialog.Description asChild>
            <div className="text-muted-foreground text-sm">{body}</div>
          </Dialog.Description>
        ) : null}
      </div>
      {children}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            onCancel?.()
            close()
          }}
        >
          {cancel}
        </Button>
        <Button
          type="button"
          variant={danger ? 'destructive' : 'default'}
          disabled={disabled}
          onClick={() => {
            onConfirm()
            close()
          }}
        >
          {action}
        </Button>
      </div>
    </>
  )
}
